
import { useState, useEffect, useRef } from 'react';

const Newsletter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d3pl90uvn82kpvfp3im0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="newsletter" ref={sectionRef} className="py-20 bg-gradient-to-br from-green-600 to-emerald-700">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {/* Header */}
          <div className="mb-12">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-leaf-line text-3xl text-white"></i>
              </div>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join the Aroha <span className="text-green-200">Community</span>
            </h2>
            <p className="text-xl text-green-100 max-w-2xl mx-auto">
              Stay connected with our farm family. Get updates on seasonal produce, 
              farm events, and sustainable living tips delivered to your inbox.
            </p>
          </div>

          {/* Newsletter Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8">
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-6 py-4 rounded-full bg-white/20 border border-white/30 text-white placeholder-green-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your Email"
                    required
                    className="w-full px-6 py-4 rounded-full bg-white/20 border border-white/30 text-white placeholder-green-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-white text-green-700 px-8 py-4 rounded-full font-semibold hover:bg-green-50 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Joining...' : 'Join Our Community'}
              </button>
            </form>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-500/20 border border-green-400/30 rounded-lg">
                <p className="text-green-100">
                  <div className="w-5 h-5 inline-flex items-center justify-center mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  Thank you for joining our community! Welcome to Aroha Farm.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-500/20 border border-red-400/30 rounded-lg">
                <p className="text-red-100">
                  <div className="w-5 h-5 inline-flex items-center justify-center mr-2">
                    <i className="ri-error-warning-line"></i>
                  </div>
                  Please check your information and try again.
                </p>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-8 grid md:grid-cols-3 gap-6 text-center">
              <div className="text-green-100">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-calendar-line text-xl"></i>
                </div>
                <p className="text-sm">Seasonal Updates</p>
              </div>
              <div className="text-green-100">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-gift-line text-xl"></i>
                </div>
                <p className="text-sm">Exclusive Offers</p>
              </div>
              <div className="text-green-100">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-lightbulb-line text-xl"></i>
                </div>
                <p className="text-sm">Farming Tips</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
